<template>
  <BaseModal
    :show="show"
    title="Multibanking"
    height="85%"
    @close="$emit('update:show', false)"
    @closed="closed"
  >
    <!-- ElCarousel legt seine Folien absolut übereinander und braucht deshalb eine Höhe.
         Auf dem Handy gibt der Drawer sie über seine `size` vor, der Dialog auf dem
         Desktop nicht — dort steht sie hier. -->
    <div
      ref="swipeTarget"
      class="h-full md:h-[60vh] md:max-h-[30rem] -mx-4 flex flex-col"
    >
      <ElCarousel
        ref="carouselRef"
        height="100%"
        class="grow min-h-0"
        :autoplay="false"
        arrow="never"
        indicator-position="none"
        @change="(index: number) => slideIndex = index"
      >
        <ElCarouselItem
          v-for="item in items"
          :key="item.title"
          class="overflow-y-auto"
        >
          <div v-if="!item.slideContent" class="mx-4">
            <SvgVue v-if="item.icon" :icon="item.icon" class="mx-auto mb-8" />
            <h3 class="text-xl md:text-2xl text-center font-semibold leading-6 text-gray-900 m-0">
              {{ item.title }}
            </h3>
            <p class="text-base text-center leading-6 text-gray-800 mt-3 mb-5 md:my-5">
              {{ item.description }}
            </p>
            <ElAlert v-if="item.info" type="info" class="el-alert--primary" show-icon>{{ item.info }}</ElAlert>
          </div>
          <div v-else class="flex flex-col gap-1 bg-gray-100/80 rounded-xl divide-y-8 divide-white mx-4">
            <div v-for="content in item.slideContent" :key="content.title" class="px-4 py-3">
              <h4 class="font-medium text-gray-900">
                {{ content.title }}
              </h4>
              <p class="text-gray-800 mb-0">
                {{ content.description }}
              </p>
            </div>
          </div>
        </ElCarouselItem>
      </ElCarousel>

      <div
        v-if="!isBanksApiUser && slideIndex + 1 === items.length"
        class="w-full flex justify-center items-center gap-2 md:hidden"
      >
        <AppButton
          type="primary"
          text
          bg
          @click="$emit('update:show', false)"
        >
          Später einrichten
        </AppButton>
        <AppButton
          type="primary"
          @click="handleFinish"
        >
          Jetzt einrichten
        </AppButton>
      </div>
      <div class="flex flex-row w-full justify-center gap-2 mt-10">
        <button
          v-for="(item, index) in items"
          :key="item.title"
          class="w-3 h-3 md:w-2 md:h-2 rounded-full"
          :aria-label="`Navigationsschritt ${index + 1}: ${item.title}`"
          :class="[slideIndex === index ? 'bg-blue-500' : 'bg-gray-300/50']"
          @click="carouselRef?.setActiveItem(index)"
        />
      </div>
      <div class="hidden md:flex flex-row w-full items-center justify-end gap-4 mt-3 -mx-4">
        <AppButton
          v-if="slideIndex > 0"
          type="primary"
          text
          bg
          @click="carouselRef?.prev()"
        >
          Zurück
        </AppButton>
        <AppButton
          v-if="slideIndex + 1 < items.length"
          type="primary"
          @click="carouselRef?.next()"
        >
          Weiter
        </AppButton>
        <AppButton
          v-if="slideIndex + 1 === items.length"
          type="primary"
          @click="handleFinish"
        >
          {{ isBanksApiUser ? 'Schließen' : 'Jetzt einrichten' }}
        </AppButton>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import AppButton from '@/application/components/ui/AppButton.vue';
import { ElAlert, ElCarousel, ElCarouselItem } from 'element-plus';
import { useSwipe } from '@vueuse/core';
import { ref } from 'vue';

import SvgVue from "@/application/components/SvgVue";
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';
import BaseModal from '@/application/components/BaseModal.vue';
import { storeToRefs } from 'pinia';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { TrackEventCategory } from '@/application/utils/tracking/events.ts';

defineProps<{ show: boolean }>();
const emit = defineEmits(['update:show', 'setUp']);

const baseDataStore = useBaseDataStore();
const { duzen } = baseDataStore;
const { isBanksApiUser } = storeToRefs(baseDataStore);

const carouselRef = ref<InstanceType<typeof ElCarousel> | null>(null);
const slideIndex = ref(0);
const swipeTarget = ref<HTMLElement | null>(null);

// ElCarousel has no swipe gesture, but these slides run mostly on mobile.
useSwipe(swipeTarget, {
  onSwipeEnd: (_event, direction) => {
    if (direction === 'left') carouselRef.value?.next();
    if (direction === 'right') carouselRef.value?.prev();
  },
});

function handleFinish() {
  emit('update:show', false)

  if (!isBanksApiUser.value) {
    useTagManager().push({ event: TrackEventCategory.BANKS_API, action: 'finish-info-modal-and-setup' });
    emit('setUp');
  }
}

function closed() {
  carouselRef.value?.setActiveItem(0);
  useTagManager().push({ event: TrackEventCategory.BANKS_API, action: 'close-info-modal' });
}

const items = [
  {
    title: 'Jetzt neu: Banking im Finanzmanager',
    description: `${duzen('Verbinde deine', 'Verbinden Sie Ihre')} Bankkonten, um Kontostände und Umsätze
    direkt im Finanzmanager zu sehen und Verträge komfortabel zuzuordnen.`,
    info: `Multibanking ist ein neues Feature im Finanzmanager. Es wird stetig weiterentwickelt – vereinzelt können
    aber noch kleine Fehler auftreten.`,
    icon: 'banking/multibanking',
  },
  {
    slideContent: [
      {
        title: 'Sicher & reguliert',
        description: 'Die Verbindung erfolgt über die BANKSapi-Widgets (BaFin-reguliertes Zahlungsinstitut).'
      },
      {
        title: 'Datenschutz',
        description: `Nur ${ duzen('du siehst deine', 'Sie sehen Ihre') } Bankdaten in der App.
        ${ duzen('Dein', 'Ihr') } Versicherungsmakler hat keinen Zugriff. Der Finanzmanager speichert
        keine Bankzugangsdaten und keine Kontoumsätze/-salden dauerhaft – die Anzeige erfolgt nur temporär über BANKSapi.`
      },
      {
        title: 'Verträge finden',
        description: `Wir können aus Kontobewegungen Hinweise auf bestehende Versicherungsverträge ableiten und
        ${duzen('dir', 'Ihnen')} zur einfachen Anlage anzeigen (Details siehe Abschnitt „Verträge finden
        – kurz erklärt“ unten).`
      },
      {
        title: 'Jederzeit trennbar',
        description: `Unter „Bankverbindung verwalten“ ${duzen('kannst du deine', 'können Sie Ihre')}
        Bankverbindungen jederzeit wieder entfernen oder neu hinzufügen.`
      },
    ],
  },
  {
    title: 'Kurzanleitung: Bankkonten verknüpfen',
    description: `Das Hinzufügen ${duzen('deiner', 'Ihrer')} Bankkonten geht schnell und unkompliziert.
    ${duzen('Erfahre, wie du Bankkonten hinzufügst', 'Erfahren Sie, wie Sie Bankkonten hinzufügen')}.`,
    icon: 'banking/add-bank-account',
  },
  {
    slideContent: [
      {
        title: '1. Bankzugang hinzufügen',
        description: `${duzen('Klicke', 'Klicken Sie')} auf „Bankzugang hinzufügen“`
      },
      {
        title: '2. Bank auswählen',
        description: `${duzen('Suche und wähle deine', 'Suchen und wählen Sie Ihre')} Bank (Name, BLZ oder BIC)`
      },
      {
        title: '3. Zugangsdaten eingeben',
        description: `${duzen('Gib deine', 'Geben Sie Ihre')} Online-Banking-Daten ein und
        ${duzen('folge', 'folgen Sie')} der starken Kundenauthentifizierung
        (z. B. TAN/Banking-App). Hinweis: Die Eingabe erfolgt ausschließlich im gesicherten BANKSapi-Widget.`
      },
      {
        title: '4. Konten auswählen & einwilligen',
        description: `${duzen('Wähle', 'Wählen Sie')} die zu verknüpfenden Konten und
        ${duzen('aktiviere', 'aktivieren Sie')} die Einwilligung:
        „Hiermit willige ich ein, dass Finanzdaten nach jedem Abruf durch BANKSapi anschließend DEMV Systems GmbH
        zum Abruf zur Verfügung gestellt werden.“
        ${duzen('Klicke', 'Klicken Sie')} anschließend auf „Weiter“, um die Verknüpfung abzuschließen.`
      },
    ],
  },
  {
    title: 'Verträge finden',
    description: `Mit der Banking-Integration können Verträge in ${duzen('deinen', 'Ihren')} Abbuchungen
    ausfindig gemacht werden und anschließend mit einem Klick hinzugefügt werden.`,
    icon: 'banking/find-contract',
  },
  {
    slideContent: [
      {
        title: 'Was passiert?',
        description: `BANKSapi analysiert Kontoumsätze auf typische Verwendungszwecke und erkennt daraus Hinweise auf
        mögliche Versicherungsverträge. Die Hinweise sind nicht garantiert vollständig.`
      },
      {
        title: 'Ein-Klick-Anlage',
        description: `Bei „Vertrag anlegen“ erzeugen wir einen neuen Vertragseintrag ohne Bankdaten und füllen bekannte Felder vor
        ${duzen('(Du prüfst und kannst alles bearbeiten)', '(Sie prüfen und können alles bearbeiten)')}.
        Falsch erkannt? ${duzen('Markiere', 'Markieren Sie')} den Hinweis mit „Nicht mein Vertrag“, dann wird er ausgeblendet.`
      },
    ],
  },

]
</script>
